import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { SearchableSelectComponent } from '@app/partials/form/searchable-select/searchable-select.component';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { RouteParams } from '@app/shared/abstract/route-params';
import { BehaviorSubject, catchError, combineLatest, forkJoin, map, Observable, of, Subject, Subscription } from 'rxjs';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { VenueService } from '@app/venues/shared/venue.service';
import { UserService } from '@app/users/shared/user.service';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { EventModel } from '../shared/event.model';
import { EventService } from '../shared/event.service';
import { CheckCircle, Clock, Edit, Eye, Lock, LucideAngularModule, Mail, Phone, Plus, Trash, X } from 'lucide-angular';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ToastService } from '@app/shared/toast/toast.service';
import { ContactService } from '@app/contacts/shared/contact.service';
import { DatePipe, DecimalPipe, SlicePipe } from '@angular/common';
import { EventTemplateService } from '@app/event-templates/shared/event-template.service';
import { EventTemplateModel } from '@app/event-templates/shared/event-template.model';
import { EventTemplateItemModelUtils } from '@app/event-templates/shared/event-template-item.model';
import { DateUtil } from '@app/shared/utils/date.util';
import { RightSidebarComponent } from '@app/partials/right-sidebar/right-sidebar.component';
import { ContactFormComponent } from '../shared/contact-form.component';
import { TemplateSelectionFormComponent } from '../shared/template-selection-form.component';
import { PriceLockingWarningModalComponent } from '@app/partials/modal/price-locking-warning-modal.component';
import { FeatureFlags } from '@app/config/feature-flags';
import { AuthService } from '@app/auth/auth.service';
import { UserModel } from '@app/users/shared/user.model';
import { VenueQuoteService } from '@app/quotes/shared/venue-quote.service';
import { VenueQuoteModel } from '@app/quotes/shared/venue-quote.model';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { EventRoleNameService } from '@app/events/shared/event-role-name.service';
import { OwnershipNameRefs, withRetainedName } from '@app/events/shared/event-role-name.model';

type SelectedContactSummary = {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  initials: string;
};

@Component({
  selector: 'app-events-edit',
  templateUrl: './event-edit.component.html',
  providers: [provideFlatpickrDefaults()],
  imports: [
    ButtonComponent,
    SelectComponent,
    TextInputComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    FormsModule,
    DatePickerComponent,
    TimePickerComponent,
    DatePickerComponent,
    TimePickerComponent,
    TextareaComponent,
    RouterLink,
    DatePipe,
    DecimalPipe,
    SlicePipe,
    LucideAngularModule,
    RightSidebarComponent,
    ContactFormComponent,
    TemplateSelectionFormComponent,
    SearchableSelectComponent,
    PriceLockingWarningModalComponent,
  ],
})
export class EventEditComponent extends AbstractEntityEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() embeddedMode = false;
  @Input() embeddedEvent?: EventModel;
  @Output() eventUpdated = new EventEmitter<EventModel>();

  protected readonly EditIcon = Edit;
  protected readonly Eye = Eye;
  protected readonly CheckCircleIcon = CheckCircle;
  protected readonly ClockIcon = Clock;
  protected readonly XIcon = X;
  protected readonly PlusIcon = Plus;
  protected readonly LockIcon = Lock;
  protected readonly MailIcon = Mail;
  protected readonly PhoneIcon = Phone;
  protected readonly TrashIcon = Trash;

  protected processing = false;
  protected validationErrors: string[] = [];
  protected event!: EventModel;
  protected currentUser: UserModel | null = null;
  protected isAdmin = false;
  protected isManager = false;
  protected canApplyTemplates = false;
  protected canChangeStatus = false;
  protected canEditVenueOverrides = false;

  // TVG-120: when launched from the contact-page Create Event button (or any
  // other entry path that prefills `status=Opportunity` via query param), the
  // status field is locked to Opportunity. sub_status falls to `event_created`
  // automatically via the model's boot::saving hook.
  protected statusLockedToOpportunity = false;

  get showStatusLockedMessage(): boolean {
    return this.isEventConfirmed && !this.isAdmin;
  }

  get showDateLockedMessage(): boolean {
    return this.event?.status === 'Confirmed' && !this.isAdmin;
  }

  get showConfirmedDateField(): boolean {
    return this.isAdmin && this.isConfirmedStatus();
  }

  get showConfirmedDateInModal(): boolean {
    return this.isAdmin && this.showPriceLockingModal;
  }

  // Price locking modal state
  protected showPriceLockingModal = false;
  protected pendingStatusChange: string | null = null;
  protected originalStatus: string | null = null;
  protected lastSelectedStatus: string | null = null;

  private statusChangeSubscription?: Subscription;

  // VG-342: Computed property for event confirmation status (respects feature flag)
  get isEventConfirmed(): boolean {
    return FeatureFlags.PRICE_LOCKING && this.event?.status === 'Confirmed';
  }

  private isConfirmedStatus(): boolean {
    const status = this.form.get('status')?.value ?? this.event?.status;
    return status === 'Confirmed';
  }

  routeParams = new RouteParams({
    pageNum: 1,
  });

  statusChoices = [
    { value: 'Opportunity', label: 'Opportunity' },
    { value: 'Provisional', label: 'Provisional' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Closed', label: 'Closed' },
  ];

  /**
   * TVG-356: statuses a manager may give an event they are raising — mirrors
   * EventPolicy::MANAGER_CREATABLE_STATUSES.
   */
  private readonly managerCreatableStatuses = ['Opportunity', 'Provisional'];

  /**
   * TVG-356: only admins may confirm an event, so Confirmed is not offered to
   * anyone else. A manager raising a new event is narrower still — the client's
   * rule is Opportunity or Provisional only. On an existing event a manager
   * keeps every non-Confirmed status, which TVG-356 does not change. An event
   * that is already Confirmed keeps the option so its current value still
   * renders for everyone.
   */
  protected get selectableStatusChoices(): { value: string; label: string }[] {
    if (this.isAdmin || this.event?.status === 'Confirmed') {
      return this.statusChoices;
    }

    if (this.isManager && this.isCreateMode) {
      return this.statusChoices.filter((choice) =>
        this.managerCreatableStatuses.includes(choice.value),
      );
    }

    return this.statusChoices.filter((choice) => choice.value !== 'Confirmed');
  }

  categoryChoices = [
    { value: 'Wedding', label: 'Wedding' },
    { value: 'Corporate Event', label: 'Corporate Event' },
    { value: 'Christmas Party', label: 'Christmas Party' },
    { value: 'Private Event', label: 'Private Event' },
    { value: 'Tasting Event', label: 'Tasting Event' },
    { value: 'Open Day', label: 'Open Day' },
    { value: 'Sales Day', label: 'Sales Day' },
  ];

  priorityChoices = [
    { value: 3, label: 'High' },
    { value: 2, label: 'Medium' },
    { value: 1, label: 'Low' },
    { value: 4, label: 'Critical' },
  ];

  form = new FormGroup(
    {
      name: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        asyncValidators: [this.uniqueValidator('name')],
        updateOn: 'blur',
      }),
      description: new FormControl('', Validators.nullValidator),
      status: new FormControl('', [
        Validators.required,
        Validators.pattern(/^(Opportunity|Provisional|Confirmed|Cancelled|Closed)$/),
      ]),
      confirmed_at: new FormControl('', []),
      category: new FormControl('', [Validators.required, Validators.pattern(/^(Wedding|Corporate Event|Christmas Party|Private Event|Tasting Event|Open Day|Sales Day)$/)]),
      priority: new FormControl('', [Validators.required]),
      start_date: new FormControl('', []),
      end_date: new FormControl('', []),
      arrival: new FormControl('', []),
      arrival_time: new FormControl('', []),
      departure: new FormControl('', []),
      departure_time: new FormControl('', []),
      show_round: new FormControl('', []),
      show_round_time: new FormControl('', []),
      bride: new FormControl('', Validators.maxLength(255)),
      groom: new FormControl('', Validators.maxLength(255)),
      brides_parents: new FormControl('', Validators.maxLength(255)),
      grooms_parents: new FormControl('', Validators.maxLength(255)),
      maid_of_honour: new FormControl('', Validators.maxLength(255)),
      bridesmaids: new FormControl('', Validators.maxLength(255)),
      flower_girls: new FormControl('', Validators.maxLength(255)),
      flower_girls_walking: new FormControl(null),
      paige_boys: new FormControl('', Validators.maxLength(255)),
      paige_boys_walking: new FormControl(null),
      best_men: new FormControl('', Validators.maxLength(255)),
      groomsmen: new FormControl('', Validators.maxLength(255)),
      bride_escort: new FormControl('', Validators.maxLength(255)),
      bridal_party_entrance_order: new FormControl('', Validators.maxLength(255)),
      menu_code_string: new FormControl('', Validators.maxLength(255)),
      special_offers_string: new FormControl('', Validators.maxLength(255)),
      sales_manager_id: new FormControl(''),
      event_manager_id: new FormControl(''),
      // TVG-83: admin-managed name lists (free text, not user-linked). Only
      // wired into the UI when the event-ownership-fields toggle is on.
      sales_manager_name_id: new FormControl<number | null>(null),
      show_around_name_id: new FormControl<number | null>(null),
      venue_id: new FormControl('', [Validators.required]),
      venue_quote_id: new FormControl<number | null>(null),
      day_guests: new FormControl<number | null>(null, [Validators.min(0)]),
      evening_guests: new FormControl<number | null>(null, [Validators.min(0)]),
      show_on_board: new FormControl(false),
      minimum_spend_override: new FormControl<number | null>(null),
      hire_price_override: new FormControl<number | null>(null),
      reservation_fee: new FormControl(null),
      deposit: new FormControl(null),
      contacts_ids: new FormControl<number[]>([]),
    },
    {},
  );

  protected managerChoices$: Observable<{ value: any; label: string }[]>;
  // TVG-83: choices for the admin-managed Sales Manager / Show around lists.
  protected salesManagerNameChoices$: Observable<{ value: any; label: string }[]>;
  protected showAroundNameChoices$: Observable<{ value: any; label: string }[]>;
  /**
   * TVG-312: the name-list entries this event already points at, re-emitted
   * whenever the event loads. A retired entry is no longer returned by the API,
   * so it is grafted back onto its own dropdown from here — see
   * {@see withRetainedName}.
   */
  private readonly ownershipNameRefs$ = new BehaviorSubject<OwnershipNameRefs>({});
  protected venueChoices$: Observable<{ value: any; label: string }[]>;
  protected contactChoices: { value: any; label: string }[] = [];
  protected initialContactChoices: { value: any; label: string }[] = [];
  protected selectedContacts: SelectedContactSummary[] = [];
  protected contactSearchFunction: (term: string, page?: number) => Observable<{ options: { value: any; label: string }[], hasMore: boolean }>;
  protected contactLinkGenerator: (option: { value: any; label: string }) => string | null;
  protected templateChoices$: Observable<{ value: number; label: string }[]>;

  templateControl = new FormControl(null);
  selectedTemplate: EventTemplateModel | null = null;

  private validationSubject = new Subject<{ field: string; value: any }>();

  seatingPlanUrl: string | ArrayBuffer | null = null;
  seatingPlanFile: File | null = null;

  // Daily price display variables
  dailyMinimumSpend: number | null = null;
  dailyHirePrice: number | null = null;
  showDailyPrices: boolean = false;
  isRightSidebarOpen = false;
  rightSidebarContent: string | null = null;
  applyingTemplate = false;
  private contactsById = new Map<number, UserModel>();
  private pendingSelectedContactLoads = new Set<number>();


  constructor(
    private eventService: EventService,
    private venueService: VenueService,
    private userService: UserService,
    private contactService: ContactService,
    private eventTemplateService: EventTemplateService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private toastService: ToastService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private venueQuoteService: VenueQuoteService,
    private featureToggleService: FeatureToggleService,
    private eventRoleNameService: EventRoleNameService,
  ) {
    super();

    this.managerChoices$ = this.userService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
        }),
      )
      .pipe(
        map((res) => res.data),
        map((users) => users.filter((user) => user.roles.includes('manager'))),
        map((users) => users.map((user) => ({ value: user.id, label: user.name }))),
      );

    // TVG-83: populate the admin-managed name-list dropdowns. Only the active
    // names are selectable on the event form; a leaver's deactivated name stays
    // on historic events but is no longer offered here. Left empty when the
    // toggle is off so the legacy user-linked Sales Manager select is used.
    // TVG-312: the name this event already holds is kept as an option even once
    // it has been retired, so a past record still shows its attribution.
    this.salesManagerNameChoices$ = of([]);
    this.showAroundNameChoices$ = of([]);
    if (this.ownershipFieldsEnabled) {
      this.salesManagerNameChoices$ = combineLatest([
        this.eventRoleNameService.getByRole('sales_manager').pipe(map((res) => res.data)),
        this.ownershipNameRefs$,
      ]).pipe(map(([entries, refs]) => withRetainedName(entries, refs.sales_manager)));

      this.showAroundNameChoices$ = combineLatest([
        this.eventRoleNameService.getByRole('show_around').pipe(map((res) => res.data)),
        this.ownershipNameRefs$,
      ]).pipe(map(([entries, refs]) => withRetainedName(entries, refs.show_around)));
    }

    this.venueChoices$ = this.venueService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
        }),
      )
      .pipe(
        map((res) => res.data),
        map((venues) => venues.map((venue) => ({ value: venue.id, label: venue.name }))),
      );

    // Set up contact search function for lazy loading
    this.contactSearchFunction = (term: string, page: number = 1) => {
      const params = new RouteParams({
        pageSize: 20,
        pageNum: page,
        filter: term ? [{ field: 'search', value: term }] : []
      });

      return this.contactService.getPage(params).pipe(
        map((response) => {
          this.storeContacts(response.data);

          return {
            options: response.data.map((contact) => this.toContactChoice(contact)),
            hasMore: response.meta.last_page ? page < response.meta.last_page : false,
          };
        })
      );
    };

    this.templateChoices$ = this.eventTemplateService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
        }),
      )
      .pipe(
        map((res) => res.data),
        map((templates) => templates.map((template) => ({
          value: template.id!,
          label: template.name || `Template ${template.id}`
        }))),
      );

    // Handle template selection
    this.templateControl.valueChanges.subscribe((templateId) => {
      if (!this.canApplyTemplates) {
        return;
      }

      if (templateId && !this.event.id) {
        this.applyTemplate(templateId);
      } else if (!templateId) {
        this.selectedTemplate = null;
      }
    });

    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    this.form.get('venue_id')!.valueChanges.subscribe(value => {
      this.checkAndUpdatePricing();
    });
    this.form.get('contacts_ids')?.valueChanges.subscribe(() => {
      this.syncSelectedContacts();
    });

    this.syncTemplateControlAccess();

    // Set up contact link generator
    this.contactLinkGenerator = (option: { value: any; label: string }) => {
      // Check if option has an email (contacts have format "Name (email)")
      const emailMatch = option.label.match(/\(([^)]+)\)$/);
      if (emailMatch) {
        return `/contacts/${option.value}/edit`;
      }
      return null;
    };
  }


  private uniqueValidator(field: string): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value || control.value === this.event[field]) {
        return of(null);
      }
      return this.eventService
        .fieldIsUnique(field, control.value)
        .pipe(map((res) => (res.is_unique ? null : { unique: true })));
    };
  }


  private prepareSaveData(): FormData {
    const formData = new FormData();
    const formValues = this.form.getRawValue(); // getRawValue() includes disabled controls

    // Combine date + time fields for backend
    const arrivalDateTime = DateUtil.combineDateTime(formValues.arrival || null, formValues.arrival_time || null);
    const departureDateTime = DateUtil.combineDateTime(formValues.departure || null, formValues.departure_time || null);
    const showRoundDateTime = DateUtil.combineDateTime(formValues.show_round || null, formValues.show_round_time || null);

    // Skip the separate time fields when building FormData
    const skipFields = ['arrival_time', 'departure_time', 'show_round_time'];

    Object.keys(this.form.controls).forEach((key) => {
      if (skipFields.includes(key)) {
        return; // Skip separate time fields
      }

      let value = formValues[key as keyof typeof formValues];

      // Replace split date fields with combined datetime values
      if (key === 'arrival' && arrivalDateTime) {
        value = arrivalDateTime;
      } else if (key === 'departure' && departureDateTime) {
        value = departureDateTime;
      } else if (key === 'show_round' && showRoundDateTime) {
        value = showRoundDateTime;
      }

      if (value === null || value === undefined) {
        formData.append(key, '');
      } else if (Array.isArray(value)) {
        if (value.length === 0) {
          formData.append(`${key}[]`, '');
        } else {
          value.forEach((item) => {
            formData.append(`${key}[]`, item.toString());
          });
        }
      } else {
        formData.append(key, String(value));
      }
    });

    if (this.seatingPlanFile) {
      formData.append('seating_plan', this.seatingPlanFile);
    }

    // Include selected template for new events
    if (!this.event?.id && this.templateControl.value) {
      formData.append('template_id', String(this.templateControl.value));
    }

    return formData;
  }

  private setupStatusChangeMonitoring(): void {
    const statusControl = this.form.get('status');

    if (!statusControl) {
      return;
    }

    this.lastSelectedStatus = statusControl.value;

    this.statusChangeSubscription?.unsubscribe();
    this.statusChangeSubscription = statusControl.valueChanges.subscribe((newStatus) => {
      if (this.shouldBlockStatusChange(newStatus)) {
        statusControl.setValue(this.lastSelectedStatus, { emitEvent: false });
        this.toastService.error('Cannot change status of confirmed events. Products are locked at confirmed pricing.');
        return;
      }

      if (this.shouldShowPriceLockingWarning(newStatus)) {
        this.pendingStatusChange = newStatus;
        this.syncConfirmedAtAccess();
        this.cdr.detectChanges();
        this.showPriceLockingModal = true;
        statusControl.setValue(this.lastSelectedStatus, { emitEvent: false });
        return;
      }

      this.lastSelectedStatus = newStatus;
      this.syncConfirmedAtAccess();
    });
  }

  private shouldShowPriceLockingWarning(newStatus: string | null): boolean {
    if (!newStatus) {
      return false;
    }

    return newStatus === 'Confirmed' && newStatus !== this.lastSelectedStatus;
  }

  private shouldBlockStatusChange(newStatus: string | null): boolean {
    // VG-342: Only block status changes when price locking is enabled
    // Block any status change FROM Confirmed status unless admin/manager
    if (!FeatureFlags.PRICE_LOCKING) {
      return false;
    }
    if (this.originalStatus === 'Confirmed' && newStatus !== 'Confirmed') {
      return !(this.isAdmin || this.isManager);
    }
    return false;
  }

  onPriceLockingConfirmed(): void {
    const statusControl = this.form.get('status');

    if (this.pendingStatusChange && statusControl) {
      statusControl.setValue(this.pendingStatusChange, { emitEvent: false });
      statusControl.markAsDirty();
      this.form.markAsDirty();
      this.lastSelectedStatus = this.pendingStatusChange;

      this.syncConfirmedAtAccess();
      this.showPriceLockingModal = false;
      this.pendingStatusChange = null;

      if (!this.embeddedMode) {
        this.saveAndContinue();
      }
    }
  }

  onPriceLockingCancelled(): void {
    this.showPriceLockingModal = false;
    this.pendingStatusChange = null;
    this.syncConfirmedAtAccess();
  }

  /**
   * TVG-120: When the CRM Sales Flow toggle is on and the form is being saved
   * with no Sales Manager assigned, prompt the user to confirm before posting.
   * Returns true if the user confirms (or the prompt is not applicable),
   * false to abort the save. Non-blocking: backend stays permissive.
   */
  private async confirmMissingSalesManager(): Promise<boolean> {
    if (!this.featureToggleService.isEnabled('crm-sales-flow')) {
      return true;
    }
    const salesManagerId = this.form.get('sales_manager_id')?.value;
    if (salesManagerId !== null && salesManagerId !== undefined && salesManagerId !== '') {
      return true;
    }
    return this.confirmationModal.confirm({
      title: 'No Sales Manager assigned',
      message: 'No Sales Manager assigned — continue?',
      confirmLabel: 'Yes, continue',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'primary',
    });
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!(await this.confirmMissingSalesManager())) {
      return;
    }
    this.processing = true;

    this.eventService.saveFormData(this.prepareSaveData(), this.event?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.setEvent(res.data);
        this.form.markAsPristine();

        // Clear template selection after successful save
        if (this.templateControl.value && this.selectedTemplate) {
          this.templateControl.setValue(null);
          this.selectedTemplate = null;
        }

        this.toastService.success(this.getSuccessMessage());
        if (this.embeddedMode) {
          this.eventUpdated.emit(res.data);
          return;
        }
        this.router.navigate(['/events']);
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error(this.extractSaveErrorMessage(res));
      },
    });
  }

  async saveAndView() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!(await this.confirmMissingSalesManager())) {
      return;
    }
    this.processing = true;

    this.eventService.saveFormData(this.prepareSaveData(), this.event?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.setEvent(res.data);
        this.form.markAsPristine();

        // Clear template selection after successful save
        if (this.templateControl.value && this.selectedTemplate) {
          this.templateControl.setValue(null);
          this.selectedTemplate = null;
        }

        this.toastService.success(this.getSuccessMessage());
        this.router.navigate(['/events', res.data.id]);
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error(this.extractSaveErrorMessage(res));
      },
    });
  }

  async saveAndContinue() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!(await this.confirmMissingSalesManager())) {
      return;
    }
    this.processing = true;

    this.eventService.saveFormData(this.prepareSaveData(), this.event?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.setEvent(res.data);
        this.form.markAsPristine();

        // Clear template selection after successful save
        if (this.templateControl.value && this.selectedTemplate) {
          this.templateControl.setValue(null);
          this.selectedTemplate = null;
        }

        this.toastService.success(this.getSuccessMessage());

        if (this.embeddedMode) {
          this.eventUpdated.emit(res.data);
          return;
        }

        if (!this.event?.id && res.data) {
          this.router.navigate(['/events', res.data.id]);
        }
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error(this.extractSaveErrorMessage(res));
      },
    });
  }

  private getSuccessMessage(): string {
    if (this.templateControl.value && this.selectedTemplate) {
      return `Event ${this.event?.id ? 'updated' : 'created'} with "${this.selectedTemplate.name}" template applied!`;
    }
    return this.event?.id ? 'Event updated successfully' : 'Event created successfully';
  }

  private extractSaveErrorMessage(res: any): string {
    return res?.error?.message || 'Failed to save event';
  }


  async cancel() {
    if (this.form.dirty) {
      const confirmLeave = await this.confirmationModal.confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        confirmLabel: 'Leave Page',
        cancelLabel: 'Stay Here',
        confirmButtonStyle: 'danger',
      });

      if (!confirmLeave) {
        return;
      }
    }

    this.router.navigate(['/events']);
  }

  get canSave(): boolean {
    return this.form.valid && this.form.dirty && !this.processing;
  }

  get isCreateMode(): boolean {
    return !this.event?.id;
  }

  /**
   * TVG-83: whether the admin-managed Sales Manager / Show around name lists
   * replace the legacy user-linked Sales Manager select on the event form.
   */
  protected get ownershipFieldsEnabled(): boolean {
    return this.featureToggleService.isEnabled('event-ownership-fields');
  }

  /**
   * TVG-83: the Sales Manager locks on confirmation so the historic seller
   * stays fixed. Shown read-only once the event is confirmed.
   */
  protected get salesManagerLocked(): boolean {
    return this.event?.status === 'Confirmed';
  }

  protected getSalesManagerNameDisplay(): string {
    return this.getDisplayValue(
      this.event?.['sales_manager_name_ref']?.name
      ?? this.resolveChoiceLabel(this.form.controls.sales_manager_name_id.value, this.embeddedSalesManagerNameChoices()),
    );
  }

  /** TVG-379: the latest viewing's outcome; nothing on a new event. */
  protected get viewingOutcomeLabel(): string | null {
    return (this.event as EventModel | undefined)?.['viewing_outcome_label'] ?? null;
  }

  protected getShowAroundNameDisplay(): string {
    return this.getDisplayValue(
      this.event?.['show_around_name_ref']?.name
      ?? this.resolveChoiceLabel(this.form.controls.show_around_name_id.value, this.embeddedShowAroundNameChoices()),
    );
  }

  /**
   * Fallback single-entry choice lists so a locked/read-only display can still
   * resolve the picked name from the event's embedded ref.
   */
  private embeddedSalesManagerNameChoices(): { value: any; label: string }[] {
    const ref = this.event?.['sales_manager_name_ref'];
    return ref ? [{ value: ref.id, label: ref.name }] : [];
  }

  private embeddedShowAroundNameChoices(): { value: any; label: string }[] {
    const ref = this.event?.['show_around_name_ref'];
    return ref ? [{ value: ref.id, label: ref.name }] : [];
  }

  protected getDisplayValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'string' && value.trim() === '') {
      return '-';
    }

    return String(value);
  }

  private resolveChoiceLabel(
    value: unknown,
    choices: Array<{ value: number | string; label: string }>,
  ): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const selectedChoice = choices.find((choice) => String(choice.value) === String(value));
    return selectedChoice?.label ?? null;
  }

  private setEvent(event: any) {
    const previousSelectedContactIds = ((this.form.get('contacts_ids')?.value as Array<number | string> | null) || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    const rawContactIds = Array.isArray(event.contacts_ids)
      ? event.contacts_ids
      : Array.isArray(event['contacts'])
        ? event['contacts'].map((contact: any) => contact.id)
        : previousSelectedContactIds;
    const normalizedContactIds = rawContactIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id));

    this.event = {
      ...event,
      contacts_ids: normalizedContactIds,
    };

    // TVG-312: refresh the retained name-list options for the loaded event.
    this.ownershipNameRefs$.next({
      sales_manager: this.event['sales_manager_name_ref'] ?? null,
      show_around: this.event['show_around_name_ref'] ?? null,
    });

    // Use DateUtil for consistent date parsing and splitting
    const arrivalSplit = DateUtil.splitDateTime(event.arrival);
    const departureSplit = DateUtil.splitDateTime(event.departure);
    const showRoundSplit = DateUtil.splitDateTime(event.show_round);

    const data = {
      ...event,
      venue_id: parseInt(event.venue_id || event.venue?.id), // Ensure venue_id is set as number
      start_date: DateUtil.parseDateToString(event.start_date),
      end_date: DateUtil.parseDateToString(event.end_date),
      arrival: arrivalSplit.date,
      arrival_time: arrivalSplit.time,
      departure: departureSplit.date,
      departure_time: departureSplit.time,
      show_round: showRoundSplit.date,
      show_round_time: showRoundSplit.time,
      confirmed_at: DateUtil.parseDateToString(event.confirmed_at),
    };


    this.form.patchValue(data);
    this.form.get('contacts_ids')?.setValue(normalizedContactIds);
    if (Array.isArray(this.event['contacts'])) {
      this.storeContacts(this.event['contacts']);
    } else {
      this.syncSelectedContacts();
    }

    // Store original status for comparison
    this.originalStatus = event.status;

    // Ensure the status control respects both role-based access and price-locking rules
    this.syncStatusControlAccess();

    // Set up status change monitoring for price locking
    this.setupStatusChangeMonitoring();

    this.loadInitialContacts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.embeddedMode) {
      return;
    }

    if (changes['embeddedEvent']?.currentValue) {
      this.setEvent(changes['embeddedEvent'].currentValue);
    }
  }

  ngOnInit(): void {
    if (this.embeddedMode) {
      if (this.embeddedEvent) {
        this.setEvent(this.embeddedEvent);
      }

      const embeddedEventId = this.embeddedEvent?.id ?? this.event?.id;
      if (embeddedEventId) {
        this.eventService.get(embeddedEventId.toString()).subscribe({
          next: (response) => {
            this.setEvent(response.data);
          },
          error: () => {
            if (this.embeddedEvent) {
              this.setEvent(this.embeddedEvent);
            }
          },
        });
      }
    } else {
      this.initializeStandaloneEvent();
    }

    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
        const roles = (user?.roles ?? []).map((role: any) => {
          if (typeof role === 'string') {
            return role.toLowerCase();
          }
          if (role && typeof role.name === 'string') {
            return role.name.toLowerCase();
          }
          return '';
        });
        this.isAdmin = roles.includes('admin');
        this.isManager = roles.includes('manager');
        this.canApplyTemplates = this.isAdmin || this.isManager;
        this.canChangeStatus = this.isAdmin || this.isManager;
        this.canEditVenueOverrides = this.isAdmin;
        this.syncTemplateControlAccess();
        this.syncVenueOverrideAccess();
        this.syncStatusControlAccess();
        this.syncConfirmedAtAccess();
        this.syncEventDateAccess();
      },
      error: () => {
        this.isAdmin = false;
        this.isManager = false;
        this.canApplyTemplates = false;
        this.canChangeStatus = false;
        this.canEditVenueOverrides = false;
        this.syncTemplateControlAccess();
        this.syncVenueOverrideAccess();
        this.syncStatusControlAccess();
        this.syncConfirmedAtAccess();
        this.syncEventDateAccess();
      },
    });

    // Initialize daily pricing display for existing events
    this.checkAndUpdatePricing();
  }

  ngOnDestroy(): void {
    this.statusChangeSubscription?.unsubscribe();
  }

  private initializeStandaloneEvent(): void {
    if (this.route.snapshot.data['event']) {
      this.setEvent(this.route.snapshot.data['event']);

      return;
    }

    const event = {} as EventModel;
    event.status = 'Opportunity';
    event.category = 'Wedding';
    event.priority_name = 'Medium';
    event.priority = 2;
    event.seating_plans = [];
    event.notes = [];
    event.products = [];
    event.reservation_fee = 2000;
    event.deposit = 500;
    this.setEvent(event);
    this.prefillFromQuote();
    this.applyCreateEventQueryParams();
  }

  /**
   * TVG-120: Read the Create Event entry-point query params and pre-populate
   * the form. Supported params:
   *   contact_id       — single contact ID to prelink on the event_users pivot
   *   status           — pre-set status (only 'Opportunity' is honoured; locks
   *                       the status dropdown for this entry path)
   *   venue_quote_id   — optional source quote whose products get imported on
   *                       store (handled by EventController::store today).
   *
   * Toggle-off contract: this helper is unguarded; it's just patchValue and
   * a disable() call, harmless for legacy callers that never send the params.
   */
  private applyCreateEventQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const contactIdRaw = params.get('contact_id');
    const statusRaw = params.get('status');
    const venueQuoteIdRaw = params.get('venue_quote_id');

    if (contactIdRaw) {
      const contactId = Number(contactIdRaw);
      if (Number.isFinite(contactId) && contactId > 0) {
        this.form.get('contacts_ids')?.setValue([contactId]);
        this.event.contacts_ids = [contactId];
      }
    }

    if (statusRaw === 'Opportunity') {
      this.form.get('status')?.setValue('Opportunity');
      this.statusLockedToOpportunity = true;
      this.syncStatusControlAccess();
    }

    if (venueQuoteIdRaw) {
      const quoteId = Number(venueQuoteIdRaw);
      if (Number.isFinite(quoteId) && quoteId > 0) {
        this.form.get('venue_quote_id')?.setValue(quoteId);
      }
    }
  }

  onSeatingPlanFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      this.seatingPlanFile = file;
      this.seatingPlanUrl = reader.result;
      reader.readAsDataURL(file);
      this.form.markAsDirty();
    }
  }

  private applyTemplate(templateId: number): void {
    if (!this.canApplyTemplates) {
      return;
    }

    this.eventTemplateService.get(templateId.toString()).subscribe({
      next: (response) => {
        const template = response.data || response;

        // Set event type to match template
        if (template.event_type) {
          this.form.patchValue({
            category: template.event_type
          });
        }

        // Store the selected template for visual display
        this.selectedTemplate = template;

        const templateName = template.name || 'Selected template';
        this.notificationService.success(`Template "${templateName}" selected`);
      },
      error: (error) => {
        console.error('Error loading template:', error);
        this.notificationService.error('Failed to load template');
        this.templateControl.setValue(null);
      }
    });
  }

  clearTemplate(): void {
    this.templateControl.setValue(null);
    this.selectedTemplate = null;
    this.notificationService.info('Template selection cleared');
  }

  formatTime(timeString: string): string {
    return EventTemplateItemModelUtils.formatTimeOfDay(timeString);
  }

  private checkAndUpdatePricing(): void {
    const venueId = this.form.get('venue_id')!.value;
    const startDate = this.form.get('start_date')!.value;

    if (venueId && startDate) {
      this.updateVenuePricing(Number(venueId), startDate);
    } else {
      this.clearDailyPrices();
    }
  }

  private prefillFromQuote(): void {
    const params = this.route.snapshot.queryParamMap;
    const quoteId = params.get('quoteId');
    if (!quoteId || this.event?.id) {
      return;
    }

    // TVG-173: the "Convert to event" action on a specific quote date passes
    // that chosen date through so it seeds Event Date, rather than always
    // falling back to the quote's preferred / first date.
    const requestedDate = params.get('eventDate');

    this.venueQuoteService.getById(quoteId).subscribe({
      next: (response) => {
        const quote: VenueQuoteModel = response.data;
        const seededDate = this.resolveQuoteSeedDate(quote, requestedDate);
        const eventName = quote.contact_name
          ? `${quote.contact_name} - ${seededDate ?? 'Quote'}`
          : `Quote ${quote.id}`;
        const descriptionParts = [`Imported from quote #${quote.id}`];
        if (quote.contact_email) {
          descriptionParts.push(`Contact: ${quote.contact_email}`);
        }

        this.event.venue_id = quote.venue_id;
        this.event.venue_quote_id = quote.id;
        this.event.day_guests = quote.day_guests ?? null;
        this.event.evening_guests = quote.evening_guests ?? null;
        this.event.start_date = seededDate as any;
        this.event.end_date = seededDate as any;
        this.event.name = eventName;

        this.form.patchValue({
          venue_id: quote.venue_id as any,
          venue_quote_id: quote.id,
          day_guests: quote.day_guests ?? null,
          evening_guests: quote.evening_guests ?? null,
          start_date: seededDate,
          end_date: seededDate,
          name: eventName,
          description: descriptionParts.join('. '),
        });

        this.checkAndUpdatePricing();
      },
      error: () => {
        // Silently ignore if quote fetch fails; user can fill manually.
      },
    });
  }

  /**
   * TVG-173: pick which quote date seeds Event Date. Honour the specific date
   * the "Convert to event" action asked for, but only when it is genuinely one
   * of the quote's candidate dates (guards against an arbitrary date arriving
   * via the query string). Otherwise fall back to the historic behaviour of the
   * preferred date, then the first selected date.
   */
  private resolveQuoteSeedDate(quote: VenueQuoteModel, requestedDate: string | null): string | null {
    if (requestedDate) {
      const candidateDates = new Set<string>([
        ...(quote.selected_dates ?? []),
        ...(quote.comparison_rows?.map((row) => row.date) ?? []),
        ...(quote.preferred_date ? [quote.preferred_date] : []),
      ]);

      if (candidateDates.has(requestedDate)) {
        return requestedDate;
      }
    }

    return quote.preferred_date || quote.selected_dates?.[0] || null;
  }

  private clearDailyPrices(): void {
    this.dailyMinimumSpend = null;
    this.dailyHirePrice = null;
    this.showDailyPrices = false;
  }

  public onDateChanged(newDate: string): void {
    this.checkAndUpdatePricing();
  }

  private updateVenuePricing(venueId: number, startDate: string): void {
    this.venueService.getVenueDailyPriceForDate(venueId, startDate).subscribe({
      next: (response) => {
        const priceForDate = response.data;
        this.dailyMinimumSpend = priceForDate.minimum_spend;
        this.dailyHirePrice = priceForDate.hire_price;
        this.showDailyPrices = true;
      },
      error: (error) => {
        this.dailyMinimumSpend = null;
        this.dailyHirePrice = null;
        this.showDailyPrices = false;
      }
    });
  }

  private syncVenueOverrideAccess(): void {
    const controls = ['minimum_spend_override', 'hire_price_override'];
    controls.forEach((key) => {
      const control = this.form.get(key);
      if (!control) {
        return;
      }

      if (this.canEditVenueOverrides) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  private syncConfirmedAtAccess(): void {
    const control = this.form.get('confirmed_at');
    if (!control) {
      return;
    }

    if (this.isAdmin && (this.isConfirmedStatus() || this.pendingStatusChange === 'Confirmed')) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private syncStatusControlAccess(): void {
    const statusControl = this.form.get('status');

    if (!statusControl) {
      return;
    }

    if (!this.canChangeStatus) {
      statusControl.disable({ emitEvent: false });
      return;
    }

    // TVG-120: Status is locked to Opportunity in the Create Event entry path.
    if (this.statusLockedToOpportunity) {
      statusControl.disable({ emitEvent: false });
      return;
    }

    if (FeatureFlags.PRICE_LOCKING && this.event?.status === 'Confirmed' && !this.isAdmin) {
      statusControl.disable({ emitEvent: false });
    } else {
      statusControl.enable({ emitEvent: false });
    }
  }

  private syncEventDateAccess(): void {
    const startDateControl = this.form.get('start_date');
    if (!startDateControl) {
      return;
    }

    const isConfirmed = this.event?.status === 'Confirmed';

    if (isConfirmed && !this.isAdmin) {
      startDateControl.disable({ emitEvent: false });
    } else {
      startDateControl.enable({ emitEvent: false });
    }
  }

  private syncTemplateControlAccess(): void {
    if (this.canApplyTemplates) {
      this.templateControl.enable({ emitEvent: false });
    } else {
      this.templateControl.disable({ emitEvent: false });
    }
  }

  openContactModal(): void {
    this.isRightSidebarOpen = true;
    this.rightSidebarContent = 'contact-form';
  }

  closeRightSidebar(): void {
    this.isRightSidebarOpen = false;
    this.rightSidebarContent = null;
  }

  onContactCreated(contact: {id: number, name: string}): void {
    // Add the new contact to the current selection
    const currentContacts = this.form.get('contacts_ids')?.value || [];
    this.form.get('contacts_ids')?.setValue([...currentContacts, contact.id]);

    // Mark form as dirty since we've changed the contacts
    this.form.markAsDirty();

    // Refresh the contact choices to include the new contact
    this.loadInitialContacts();

    this.closeRightSidebar();
    this.notificationService.success(`Contact "${contact.name}" created and added to event`);
  }

  onContactLinkClick(event: { option: { value: any; label: string }, event: Event }): void {
    const contactId = event.option.value;
    this.openContactInNewTab(contactId);
  }

  protected editSelectedContact(contactId: number): void {
    this.openContactInNewTab(contactId);
  }

  protected removeSelectedContact(contactId: number): void {
    const currentContacts = (this.form.get('contacts_ids')?.value || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    this.form.get('contacts_ids')?.setValue(currentContacts.filter((id) => id !== contactId));
    this.form.get('contacts_ids')?.markAsDirty();
    this.form.markAsDirty();
  }

  private openContactInNewTab(contactId: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/contacts', contactId, 'edit'])
    );

    // Open the contact page in a new tab
    window.open(url, '_blank');
  }

  openTemplateSelector(): void {
    if (!this.canApplyTemplates) {
      return;
    }
    this.isRightSidebarOpen = true;
    this.rightSidebarContent = 'template-selection';
  }

  onTemplateApplied(template: EventTemplateModel): void {
    this.applyingTemplate = false;
    this.closeRightSidebar();

    // Optionally reload the event to show any changes
    if (this.event?.id) {
      this.eventService.get(this.event.id.toString()).subscribe({
        next: (response) => {
          this.setEvent(response.data);
        },
        error: (error) => {
          console.error('Error reloading event:', error);
        }
      });
    }
  }

  private loadInitialContacts() {
    const params = new RouteParams({
      pageSize: 20,
      pageNum: 1,
    });

    this.contactService.getPage(params).subscribe((response) => {
      let contacts = response.data;
      this.storeContacts(contacts);

      // If we have selected contacts that might not be in the first page, fetch them specifically
      const selectedContactIds = ((this.form.get('contacts_ids')?.value as Array<number | string> | null) || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      if (selectedContactIds.length > 0) {
        // Check if any selected contacts are missing from the initial load
        const loadedContactIds = contacts.map((c) => Number(c.id)).filter((id) => Number.isFinite(id));
        const missingContactIds = selectedContactIds.filter((id) => !loadedContactIds.includes(id));

        if (missingContactIds.length > 0) {
          // Fetch the missing selected contacts individually
          const missingContactRequests = missingContactIds.map((id) =>
            this.contactService.get(id.toString()).pipe(
              map((singleContactResponse) => singleContactResponse.data),
            )
          );

          forkJoin(missingContactRequests).subscribe((missingContacts) => {
            // Combine initial contacts with missing selected ones
            const allContacts = [...contacts, ...missingContacts];
            this.storeContacts(allContacts);
            this.initialContactChoices = this.mapContactsToChoices(allContacts);
          });
          return;
        }
      }

      // No missing contacts, just use the initial load
      this.initialContactChoices = this.mapContactsToChoices(contacts);
    });
  }

  private mapContactsToChoices(contacts: UserModel[]): { value: number; label: string }[] {
    const uniqueContacts = new Map<number, UserModel>();

    contacts.forEach((contact) => {
      const contactId = Number(contact?.id);
      if (Number.isFinite(contactId) && !uniqueContacts.has(contactId)) {
        uniqueContacts.set(contactId, contact);
      }
    });

    return Array.from(uniqueContacts.values()).map((contact) => this.toContactChoice(contact));
  }

  protected getContactMailto(contact: SelectedContactSummary): string {
    return `mailto:${contact.email}`;
  }

  protected getContactTel(contact: SelectedContactSummary): string {
    const normalizedPhone = (contact.phone ?? '').replace(/[^+\d]/g, '');
    return `tel:${normalizedPhone}`;
  }

  private storeContacts(contacts: UserModel[]): void {
    contacts.forEach((contact) => {
      const contactId = Number(contact?.id);
      if (Number.isFinite(contactId)) {
        this.contactsById.set(contactId, contact);
      }
    });

    this.syncSelectedContacts();
  }

  private syncSelectedContacts(): void {
    const selectedIds = ((this.form.get('contacts_ids')?.value as Array<number | string> | null) || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    this.selectedContacts = selectedIds
      .map((id) => this.toSelectedContactSummary(this.contactsById.get(id)))
      .filter((contact): contact is SelectedContactSummary => contact !== null);

    const missingContactIds = selectedIds.filter((id) => !this.contactsById.has(id) && !this.pendingSelectedContactLoads.has(id));
    if (missingContactIds.length > 0) {
      this.loadContactsByIds(missingContactIds);
    }
  }

  private loadContactsByIds(contactIds: number[]): void {
    const uniqueContactIds = [...new Set(contactIds)].filter((id) => Number.isFinite(id));
    if (uniqueContactIds.length === 0) {
      return;
    }

    uniqueContactIds.forEach((id) => this.pendingSelectedContactLoads.add(id));

    const contactRequests = uniqueContactIds.map((id) =>
      this.contactService.get(id.toString()).pipe(
        map((response) => response.data as UserModel),
        catchError(() => of(null)),
      ),
    );

    forkJoin(contactRequests).subscribe((contacts) => {
      uniqueContactIds.forEach((id) => this.pendingSelectedContactLoads.delete(id));

      const loadedContacts = contacts.filter((contact): contact is UserModel => contact !== null);
      if (loadedContacts.length === 0) {
        return;
      }

      this.storeContacts(loadedContacts);
      this.initialContactChoices = this.mapContactsToChoices(Array.from(this.contactsById.values()));
    });
  }

  private toSelectedContactSummary(contact?: UserModel): SelectedContactSummary | null {
    if (!contact) {
      return null;
    }

    return {
      id: Number(contact.id),
      name: contact.name,
      email: this.getPreferredContactEmail(contact),
      phone: this.getPrimaryPhone(contact),
      initials: this.getContactInitials(contact.name),
    };
  }

  private toContactChoice(contact: UserModel): { value: number; label: string } {
    const email = this.getPreferredContactEmail(contact) ?? 'No email';
    return {
      value: Number(contact.id),
      label: `${contact.name} (${email})`,
    };
  }

  private getPreferredContactEmail(contact: UserModel): string | null {
    const email = [contact.email, contact.second_email]
      .map((value) => value?.trim())
      .find((value) => !!value);

    return email ?? null;
  }

  private getPrimaryPhone(contact: UserModel): string | null {
    const phone = (contact.phone_numbers ?? [])
      .map((value) => value?.trim())
      .find((value) => !!value);

    return phone ?? null;
  }

  private getContactInitials(name: string): string {
    const initials = name
      .split(' ')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');

    return initials || '?';
  }

  protected getForms(): FormGroup<any>[] {
    return [this.form];
  }
}
